import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from "@data/services/modules/auth/auth.service";
import { MessageService } from 'primeng/api';
import { operatesInThisPanel, resolveLandingRoute } from '@data/constants/role-landing-route.constant';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private router: Router,
    private authService: AuthService,
    private messageService: MessageService
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const currentUser = this.authService.getUser;
    if (!currentUser) {
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }

    //! Candado por IDENTIDAD, y va ANTES que el de permisos a propósito. Todo lo que sigue
    //! decide con `hasModulePermission`, así que un rol externo entra a cualquier ruta cuyo
    //! permiso resulte coincidir con los suyos — `Client`, por ejemplo, es el único rol con
    //! `get-by-id` sin `view`, justo lo que gatea `/customer/:id`, `/quote/:id` y
    //! `/quote/parameters/:id`. Que no coincida ninguna ruta más es una casualidad que nadie
    //! vigila: cada permiso que se le agregue a un rol externo puede abrir otra en silencio.
    //*
    //* Externos son Client, Broker y MasterBroker: su casa es `digital-channel`, que cubre
    //* todo lo que este panel les ofrecía (las listas de Clientes/Activos/Cotizaciones viven
    //* en su `InternalDashboard`, y los Brokers subordinados del MasterBroker en `/my-team`,
    //* que es donde ADR-0023 los puso). `Seller` NO es externo: es personal interno.
    //*
    //* Se CIERRA la sesión en vez de solo redirigir, porque los permisos viven cacheados en
    //* localStorage y dejarlos ahí conserva un token válido en el origen del panel.
    //*
    //* Espeja lo que `ProtectedRoute` (audience="internal") ya hacía en el canal digital; el
    //* panel era el único de los dos frontends internos sin el equivalente.
    if (!operatesInThisPanel(currentUser.user?.permissions?.name)) {
      this.authService.logout();
      return this.router.parseUrl('/login');
    }

    // ADR-0014: si el usuario interno tiene contraseña temporal, se le fuerza a
    // /change-password antes de acceder a cualquier ruta privada. La propia ruta
    // /change-password queda exenta para no entrar en bucle.
    if (this.authService.mustChangePassword && !state.url.startsWith('/change-password')) {
      this.router.navigate(['/change-password']);
      return false;
    }

    const module = next.data['module'];
    const permission = next.data['permission'];

    // Si el modulo no tiene un permiso asignado, se permite el acceso
    if (!module || !permission) return true;

    if (this.authService.hasModulePermission(module, permission)) {
      return true;
    }

    //* Permiso insuficiente: en vez de `return false` (que dejaba pantalla en blanco —
    //* el toast no tenía host global y no había ruta de fallback), se REDIRIGE a la
    //* landing del rol. Así un deep-link/bookmark a una ruta vedada (p.ej. Legal a `/`
    //* o a `/admin`) aterriza en su pantalla natural en vez de en la nada.
    const landing = resolveLandingRoute(this.authService.getUser?.user?.permissions?.name);

    //! Si la landing es la propia ruta denegada, o el rol no tiene destino privado
    //! ('/login'), esta sesión no abre NINGUNA ruta. Redirigir a /login sin más dejaba
    //! que NoAuthGuard nos devolviera aquí: bucle infinito que congela el navegador con
    //! la pantalla en blanco. Se cierra la sesión para cortarlo de raíz — además es lo
    //! correcto de fondo, porque los permisos viven cacheados en localStorage y solo un
    //! login nuevo los vuelve a sincronizar con la BD.
    if (!landing || landing === '/login' || landing === state.url) {
      this.authService.logout();
      return this.router.parseUrl('/login');
    }
    return this.router.parseUrl(landing);
  }
}
